// ✅ Helper function to format currency
const formatCurrency = (value) => {
  if (isNaN(value)) return null;
  return `₹${Number(value).toFixed(2)}`;
};

export const createCustomerCardData = (customer, type, paymentMode) => {
  // ---- Common Values ----
  const price = Number(customer.price) || 0;
  const quantity = Number(customer.count) || 0;
  const deliveredQty = Number(customer.delivered_quantity) || 0;
  const gstAmountFromAPI = Number(customer.gst_amount1) || 0;
  const taxableAmountFromAPI = Number(customer.taxable_amount1) || 0;
  const securityDepositPerUnit = Number(customer.security_deposit_per_unit) || 0;
  const creditDiscountAmount = Number(customer.credit_discount_amount) || 0;
  const couponDiscount = Number(customer.coupon_discount) || 0;
  const couponCodeId = Number(customer.coupon_code_id) || 0;

  // ✅ NEW: Loan flag
  const loanFlag = Number(customer.loan_flag) || 0;

  const hasCoupon = customer.coupon_code && customer.coupon_code.trim() !== "";
  const activeQuantity = type === "4" ? deliveredQty : quantity;

  // ---- Empties received ----
  const mainEmpties =
    type !== "4"
      ? Number(customer.returnclinder) || 0
      : Number(customer.empty_received) || 0;

  const hasExtraCylinders =
    Array.isArray(customer.extra_cylinders) && customer.extra_cylinders.length > 0;

  const extraEmpties = hasExtraCylinders
    ? customer.extra_cylinders.reduce(
        (sum, cyl) => sum + (parseInt(cyl.extra_received_empty) || 0),
        0
      )
    : 0;

  const totalEmptiesReceived = hasExtraCylinders
    ? mainEmpties + extraEmpties
    : mainEmpties;

  // ===============================
  //  UPDATED CONDITION
  //  When type === "1", DO NOT apply skip rule
  // ===============================
  const skipSecurityDeposit =
    type === "1" ? false : totalEmptiesReceived > deliveredQty;

  // ---- Initialize vars ----
  let totalSecurityDeposit = 0;
  let calculatedTotal = 0;
  let discountedPrice = price;
  let totalCouponDiscount = 0;

  // ====================
  // ✅ COUPON LOGIC
  // ====================
  if (hasCoupon) {
    totalCouponDiscount = couponDiscount * activeQuantity;
    discountedPrice = price - couponDiscount;

    const apiSecurityDeposit = Number(customer.securitydeposit1) || 0;

    if (loanFlag === 1 || skipSecurityDeposit) {
      totalSecurityDeposit = 0;
    } else {
      totalSecurityDeposit = apiSecurityDeposit;
    }

    calculatedTotal = taxableAmountFromAPI + gstAmountFromAPI + totalSecurityDeposit;
  }

  // ====================
  // ✅ CREDIT LOGIC
  // ====================
  else if (paymentMode === "CREDIT") {
    discountedPrice = price - creditDiscountAmount;

    const apiSecurityDeposit = Number(customer.securitydeposit1) || 0;

    if (loanFlag === 1 || skipSecurityDeposit) {
      totalSecurityDeposit = 0;
    } else {
      totalSecurityDeposit = apiSecurityDeposit;
    }

    calculatedTotal = taxableAmountFromAPI + gstAmountFromAPI + totalSecurityDeposit;
  }

  // ====================
  // ✅ NORMAL LOGIC
  // ====================
  else {
    const apiSecurityDeposit = Number(customer.securitydeposit1) || 0;

    if (loanFlag === 1 || skipSecurityDeposit) {
      totalSecurityDeposit = 0;
    } else {
      totalSecurityDeposit = apiSecurityDeposit;
    }

    calculatedTotal = taxableAmountFromAPI + gstAmountFromAPI + totalSecurityDeposit;
  }

  // ---- Display Data ----
  const allData = [
    { key: "Product Name", value: customer.product_name, valueWeight: "semibold" },
    { key: "Quantity", value: quantity },
    { key: "Delivered Quantity", value: deliveredQty },
    { key: "Cancelled Quantity", value: customer.cancelled_quantity },
    { key: "Empties Received", value: totalEmptiesReceived },
    {
      key: "Date and Time",
      value:
        customer.datetime !== "0000-00-00 00:00:00" ? customer.datetime : null,
    },
    {
      key: "Schedule Date",
      value:
        customer.scheduledate !== "0000-00-00" ? customer.scheduledate : null,
    },
    { key: "Schedule Time", value: customer.scheduletime || null },

    ...(type === "5"
      ? [
          {
            key: "Cancel Date & Time",
            value:
              customer.cancel_datetime !== "0000-00-00 00:00:00"
                ? customer.cancel_datetime
                : null,
          },
          { key: "Cancel Reason", value: customer.cancel_reason },
          { key: "Cancelled By", value: customer.cancelled_by },
        ]
      : []),

    { key: "RSP Per Unit", value: formatCurrency(price), valueWeight: "medium" },

    ...(type === "3" || type === "4"
      ? [
          {
            key: "Dispatch Date & Time",
            value:
              customer.dispatch_datetime !== "0000-00-00 00:00:00"
                ? customer.dispatch_datetime
                : null,
          },
        ]
      : []),

    ...(type === "4"
      ? [
          {
            key: "Delivered Date & Time",
            value:
              customer.deliver_datetime !== "0000-00-00 00:00:00"
                ? customer.deliver_datetime
                : null,
          },
        ]
      : []),

    ...(hasCoupon || (paymentMode === "ONLINE" && couponDiscount > 0)
      ? [
          { key: "Coupon Code", value: customer.coupon_code },
          { key: "Coupon Discount (Per Unit)", value: formatCurrency(couponDiscount) },
          { key: "Total Coupon Discount", value: formatCurrency(totalCouponDiscount) },
          { key: "Discounted Price (Per Unit)", value: formatCurrency(discountedPrice) },
          { key: "Taxable Amount", value: formatCurrency(taxableAmountFromAPI) },
          { key: "GST (18%)", value: formatCurrency(gstAmountFromAPI) },

          ...(loanFlag === 1 || skipSecurityDeposit
            ? []
            : [
                {
                  key: "Total Security Deposit",
                  value: formatCurrency(totalSecurityDeposit),
                  valueWeight: "medium",
                },
              ]),

          {
            key: "Total Amount (Final)",
            value: formatCurrency(calculatedTotal),
            valueWeight: "bold",
          },
        ]
      : paymentMode === "CREDIT"
      ? [
          {
            key: "Credit Discount (Per Unit)",
            value: formatCurrency(creditDiscountAmount),
          },
          { key: "Discounted Price (Per Unit)", value: formatCurrency(discountedPrice) },
          { key: "Taxable Amount", value: formatCurrency(taxableAmountFromAPI) },
          { key: "GST (18%)", value: formatCurrency(gstAmountFromAPI) },

          ...(loanFlag === 1 || skipSecurityDeposit
            ? []
            : [
                {
                  key: "Total Security Deposit",
                  value: formatCurrency(totalSecurityDeposit),
                  valueWeight: "medium",
                },
              ]),

          {
            key: "Total Amount (Final)",
            value: formatCurrency(calculatedTotal),
            valueWeight: "bold",
          },
        ]
      : [
          { key: "Total Price", value: formatCurrency(price * activeQuantity) },
          { key: "Taxable Amount", value: formatCurrency(taxableAmountFromAPI) },

          ...(loanFlag === 1 || skipSecurityDeposit
            ? []
            : [
                {
                  key: "Security Deposit Per Unit",
                  value: formatCurrency(securityDepositPerUnit),
                },
                {
                  key: "Total Security Deposit",
                  value: formatCurrency(totalSecurityDeposit),
                  valueWeight: "medium",
                },
              ]),

          { key: "GST Amount", value: formatCurrency(gstAmountFromAPI) },

          {
            key: "Total Amount",
            value: formatCurrency(calculatedTotal),
            valueWeight: "bold",
          },
        ]),
  ];

  const filteredData = allData.filter(
    (item) =>
      item.value !== null &&
      item.value !== undefined &&
      item.value !== "" &&
      item.value !== "₹0.00"
  );

  return {
    displayData: filteredData,
    calculatedTotal,
    taxableAmount: taxableAmountFromAPI,
    gstAmount: gstAmountFromAPI,
    totalSecurityDeposit,
    discountedPrice,
    totalCouponDiscount,
  };
};

// ==========================
// ✅ GRAND TOTAL
// ==========================
export const calculateGrandTotal = (details, type, paymentMode) => {
  if (!details || details.length === 0) return 0;

  return details.reduce((sum, product) => {
    const productData = createCustomerCardData(product, type, paymentMode);
    return sum + (productData?.calculatedTotal || 0);
  }, 0);
};
